import React from "react";
import {findDOMNode} from "react-dom";
import FieldMultiselect_PopoverOptionsSelector from "./popover-options-selector";
import SelectUserGroupingControl from "./select-user-grouping-control";
import VScroll from "../../../../../gd-components/scrolling/custom-v-scroll/v-scroll";
import Popover from "../../../../../gd-components/gd-popover/popover-v2";
import PopoverPosition from "../../../../../gd-components/gd-popover/popover-position";
import {FieldPopoverMenuProps} from "../../../dropdown/components/popover/popover-menu";
import {UsersSelectorGroupBy} from "../../@const";
import FieldMultiselect_PopoverSearch from "../../../multiselect/components/popover/popover-search";
import {buildDisplayOptions, getGroupTitles} from "./build-options";

// The picker remounts PopoverMenu when selection switches empty↔non-empty (different render branch).
// We cache collapsed state per field instance so it survives remounts.
const _collapsedCache = new WeakMap<object, Set<string>>()

export default class FieldUsers_PopoverMenu extends React.Component<FieldPopoverMenuProps,any> {

    public optionsSelector      : any
    public scroll      : any

    constructor(props:FieldPopoverMenuProps) {
        super(props);

        this.state = {
            searchString    : null,
            collapsedGroups : _collapsedCache.get(props.field as object) ?? new Set<string>(),
        }
    }

    onPopoverKeyDown = (e:React.SyntheticEvent) => {
        if (this.optionsSelector) this.optionsSelector.onPopoverKeyDown(e)
    }

    onSearchChange = (newSearchString:string) => {
        this.setState({ searchString: newSearchString });
    }

    onGroupByChange = (grouping: UsersSelectorGroupBy) => {
        gd.user.settings.interface.usersSelectorGroupBy = grouping
        gd.user.settings.save()
        this._setCollapsed(new Set<string>())
    }

    onToggleGroup = (title: string) => {
        const next = new Set<string>(this.state.collapsedGroups)
        if (next.has(title)) next.delete(title)
        else next.add(title)
        this._setCollapsed(next)
    }

    collapseAll = () => {
        const grouping = gd.user.settings.interface.usersSelectorGroupBy ?? UsersSelectorGroupBy.NONE
        const titles = getGroupTitles(this.props.field, grouping)
        this._setCollapsed(new Set<string>(titles))
    }

    expandAll = () => {
        this._setCollapsed(new Set<string>())
    }

    _setCollapsed = (set: Set<string>) => {
        _collapsedCache.set(this.props.field as object, set)
        this.setState({ collapsedGroups: set })
    }

    onClearSelection = () => {
        const {parent} = this.props
        parent.setState({ currentOptions: [] }, () => {
            parent.props.onChange?.([], false)
        })
    }

    onGroupHeaderClick = (groupValues: string[]) => {
        const {parent, field, model} = this.props
        const current: string[] = parent.state.currentOptions ?? field.getViewValue(model) ?? []

        const allSelected = groupValues.every((v: string) => current.includes(v))
        const newOptions = allSelected
            ? current.filter((v: string) => !groupValues.includes(v))
            : _.union(current, groupValues)

        parent.setState({ currentOptions: newOptions }, () => {
            parent.props.onChange?.(newOptions, false)
        })
    }

    updateScroll = (node:any) => {
        if (!this.scroll) return;

        const scrollNode : any = findDOMNode(this.scroll.childrenContainer);

        const nodeTop = node.offsetTop;
        const nodeBottom = node.offsetTop + node.offsetHeight;
        const scrollTop = scrollNode.scrollTop;
        const scrollBottom = scrollNode.scrollTop + scrollNode.offsetHeight;

        if (scrollBottom < nodeBottom) this.scroll.setScrollTop(nodeBottom - scrollNode.offsetHeight, false, 100);
        else if (scrollTop > nodeTop) this.scroll.setScrollTop(nodeTop, false, 100);
    }

    render() {
        const {parent, field, model} = this.props
        const isManager = gd.ac.check.company.isManager();
        const PopoverMenuMore = _.get(field.components, 'PopoverMenuMore', false)

        const grouping = gd.user.settings.interface.usersSelectorGroupBy ?? UsersSelectorGroupBy.NONE
        const displayOptions = buildDisplayOptions(field, this.state.searchString, grouping, this.state.collapsedGroups)
        const navigable = displayOptions.filter((o: any) => !o.type)
        const showEmpty = !!this.state.searchString && navigable.length === 0
        const selectedCount = (parent.state.currentOptions ?? field.getViewValue(model) ?? []).length

        const popoverPosition = new PopoverPosition({
            attachment      : "top center",
            targetAttachment: "bottom center",
        })

        return (
            <Popover position={popoverPosition}
                     className="min-w-250"
                     getTargetFunc={()=>findDOMNode(this.props.parent)}
                     onKeyDown={this.onPopoverKeyDown}
                     outsideClickIgnoreClass="popover"
                     onClickOutside={()=>{this.props.parent.hidePopover()}}>

                <div className="ui5-bg-gray flex-column-left-top p-10">

                    <FieldMultiselect_PopoverSearch searchable={field.isSearchable}
                                                    searchString={this.state.searchString}
                                                    onHidePopover={parent.hidePopover}
                                                    onSearchChange={this.onSearchChange}
                                                    className="p-0 w-100p"
                    />

                    <SelectUserGroupingControl grouping          = {grouping}
                                               selectedCount     = {selectedCount}
                                               onChange          = {this.onGroupByChange}
                                               onCollapseAll     = {this.collapseAll}
                                               onExpandAll       = {this.expandAll}
                                               onClearSelection  = {this.onClearSelection}/>

                </div>

                <div className={Classes("ui5-popover-menu", field.classes.PopoverMenu, {'with-help': isManager && PopoverMenuMore})}>
                    <VScroll minDragHeight={20} containerClass={field.classes.PopoverMenuScrollContainer} scrollVisibility="auto">
                        {showEmpty
                            ? <div className="ui5-txt-not-important p-15 txt-center">No users found</div>
                            : <FieldMultiselect_PopoverOptionsSelector ref={(r:any)=>this.optionsSelector = r}
                                                                       {...this.props}
                                                                       options={displayOptions}
                                                                       onUpdateScroll={this.updateScroll}
                                                                       onGroupHeaderClick={this.onGroupHeaderClick}
                                                                       onToggleGroup={this.onToggleGroup}/>
                        }
                    </VScroll>

                    {isManager && PopoverMenuMore && <PopoverMenuMore {...this.props}/>}
                </div>
            </Popover>
        );
    }
}
